import { useEffect, useRef } from 'react';

/**
 * Broadcasts an `away_state` event when the local tab becomes hidden
 * (Page Visibility API) or the window loses focus (e.g. user switches
 * apps), and again when it returns. The peer renders a ghost cursor
 * while we're away.
 *
 * Also swaps the page's favicon to a gray ghost while away — so when
 * the user peeks at their tab bar from a different tab, they see
 * the live-session tab wearing a little ghost as a gentle reminder
 * to come back. Restored to the original favicon on return / unmount.
 *
 * The third argument controls whether to *broadcast* away events. The
 * teacher's view passes `false` so they never appear as a ghost on
 * the student's screen — we only ghost-shame the student. The favicon
 * swap still happens for both sides regardless.
 *
 * An optional `onTransition({ from, to })` callback fires on every
 * local transition (regardless of `broadcastAway`). Used by the
 * student lobby to surface a brief "welcome back" toast on refocus
 * without re-instrumenting the visibility listeners here. Kept in a
 * ref so callers can pass a fresh closure on every render without
 * tearing down the listeners.
 */
export function usePresenceAway(
  perform,
  isConnected = true,
  broadcastAway = true,
  onTransition = null
) {
  const onTransitionRef = useRef(onTransition);
  useEffect(() => {
    onTransitionRef.current = onTransition;
  }, [onTransition]);

  useEffect(() => {
    if (!perform || typeof document === 'undefined') return;

    let lastSent = null;
    let lastSeen = null;
    const send = (away) => {
      if (!broadcastAway) return;
      // Don't try to broadcast until the cable subscription is open —
      // ActionCable silently drops perform() calls on a closed socket,
      // which would leave the peer with stale `away: true` state after
      // we re-mount (e.g. on a hard refresh).
      if (!isConnected) return;
      if (lastSent === away) return;
      lastSent = away;
      perform('away_state', { away });
    };

    const transition = (away) => {
      if (lastSeen === away) return;
      const cb = onTransitionRef.current;
      if (typeof cb === 'function' && lastSeen !== null) {
        cb({ from: lastSeen, to: away });
      }
      lastSeen = away;
    };

    // ── Favicon swap ────────────────────────────────────────────
    const linkEl = ensureFaviconLink();
    const originalHref = linkEl?.getAttribute('href') || null;
    const ghostHref = makeGhostFaviconDataUri();

    const setFavicon = (away) => {
      if (!linkEl) return;
      if (away) {
        linkEl.setAttribute('href', ghostHref);
      } else if (originalHref) {
        linkEl.setAttribute('href', originalHref);
      }
    };

    // ── Combined visibility watcher ─────────────────────────────
    const update = () => {
      const hidden = document.hidden || !document.hasFocus();
      send(!!hidden);
      setFavicon(!!hidden);
      transition(!!hidden);
    };

    // Run once immediately to set the favicon. The `send` call inside
    // is gated on isConnected, so this also broadcasts now if the
    // cable is already up — and re-runs (via the [isConnected] dep
    // below) the moment the connection opens for the first time.
    update();

    document.addEventListener('visibilitychange', update);
    window.addEventListener('blur', update);
    window.addEventListener('focus', update);

    return () => {
      document.removeEventListener('visibilitychange', update);
      window.removeEventListener('blur', update);
      window.removeEventListener('focus', update);
      if (linkEl && originalHref) linkEl.setAttribute('href', originalHref);
    };
  }, [perform, isConnected, broadcastAway]);
}

function ensureFaviconLink() {
  if (typeof document === 'undefined') return null;
  let link = document.querySelector('link[rel~="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'icon');
    document.head.appendChild(link);
  }
  return link;
}

// Inline SVG data URI for a small ghost favicon — drawn at a 32×32 viewBox
// so the silhouette stays legible at the 16×16 the browser actually renders.
function makeGhostFaviconDataUri() {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
      <rect width="32" height="32" rx="6" fill="#f3f4f6"/>
      <path d="M16 4 C10 4 6 8 6 14 v14 l3 -3 l3 3 l4 -3 l4 3 l3 -3 l3 3 V14 C26 8 22 4 16 4 z"
            fill="#9ca3af" stroke="#ffffff" stroke-width="1" stroke-linejoin="round"/>
      <circle cx="12" cy="14" r="1.6" fill="#ffffff"/>
      <circle cx="20" cy="14" r="1.6" fill="#ffffff"/>
    </svg>
  `.trim();
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}
